import Image from 'next/image'

import { cn } from '@/lib/utils'
import { Button } from '@/components/generals/button'
import { Reveal } from '@/components/generals/reveal'
import { Section } from '@/components/generals/section'
import { AnimatedHeading } from '@/components/generals/animated-heading'

type ServiceBlockProps = {
    id: string
    title: string
    body: string
    included: string[]
    investment: string
    image: string
    alt: string
    /** Puts the pricing card on the left, as the Google Ads block does. */
    cardLeft?: boolean
}

export const ServiceBlock = ({
    id,
    title,
    body,
    included,
    investment,
    image,
    alt,
    cardLeft = false
}: ServiceBlockProps) => {
    return (
        <Section
            id={id}
            className='py-[70px] lg:py-[90px]'
            containerClassName='items-stretch'
        >
            <div className='grid w-full grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-16'>
                <div className={cn('flex flex-col gap-6', cardLeft && 'lg:order-2')}>
                    <AnimatedHeading
                        segments={[{ text: title }]}
                        className='text-[clamp(1.75rem,3.2vw,2.625rem)] leading-[1.1] font-medium tracking-[-0.04em] text-white'
                    />

                    <Reveal
                        y={20}
                        blur={8}
                        delay={0.08}
                        className='max-w-[56ch] text-[15px] leading-[1.6] tracking-[-0.01em] text-white/56'
                    >
                        {body}
                    </Reveal>

                    <Reveal
                        y={20}
                        blur={8}
                        delay={0.12}
                        className='mt-2 flex flex-col gap-4'
                    >
                        <h3 className='text-lg leading-none font-medium tracking-[-0.02em] text-white'>
                            What&rsquo;s included?
                        </h3>

                        <ul className='flex flex-col gap-3'>
                            {included.map((item) => (
                                <li
                                    key={item}
                                    className='flex items-start gap-3 text-[15px] leading-[1.5] tracking-[-0.01em] text-white/56'
                                >
                                    <span
                                        aria-hidden='true'
                                        className='mt-2 size-1.5 shrink-0 rounded-full bg-[#8b5cf6]'
                                    />

                                    {item}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>

                <Reveal
                    y={30}
                    blur={12}
                    delay={0.06}
                    className={cn(
                        'overflow-hidden rounded-2xl bg-surface-1 ring-1 ring-white/8',
                        cardLeft && 'lg:order-1'
                    )}
                >
                    <div className='relative aspect-16/10 w-full overflow-hidden'>
                        <Image
                            src={image}
                            alt={alt}
                            fill
                            sizes='(max-width: 1024px) 100vw, 560px'
                            className='object-cover'
                        />
                    </div>

                    <div className='flex flex-col items-center gap-4 px-6 py-8 text-center sm:px-10'>
                        <h3 className='text-xl leading-none font-medium tracking-[-0.03em] text-white'>
                            Project Investment
                        </h3>

                        <p className='max-w-[48ch] text-[15px] leading-[1.6] tracking-[-0.01em] text-white/56'>
                            {investment}
                        </p>

                        <Button
                            href='/contact'
                            size='small'
                            className='mt-1'
                        >
                            Book a call
                        </Button>
                    </div>
                </Reveal>
            </div>
        </Section>
    )
}
